import React, { useState, useCallback } from "react";

import { ReducersTypes } from "../redux/reducers/Models";
import { Link } from 'react-router-dom';
import Icon from '@/atomic/atoms/Icon';
import Loading from '@/atomic/atoms/Loading';
import LoadingOverlay from '@/atomic/atoms/LoadingOverlay';
import LoginModal from '@/atomic/atoms/LoginModal';
import Modal from '@/atomic/atoms/Modal';
import Footer from '@/atomic/atoms/Footer';
import LanguageSelector from '@/atomic/atoms/LanguageSelector';
import WebViewMenu from '@/atomic/molecules/WebViewMenu';
import WebViewHeader from '@/atomic/molecules/WebViewHeader';
import { closeWebView } from '@/hooks/useIsWebView';
import environment from '@/settings/environment';
import useI18n from '@/hooks/useI18n';
import { InputMask } from '@react-input/mask';

export interface PropsView {
  toggleMenu: () => void;
  isVisibleMenu: boolean;
  user: ReducersTypes['userReducer'];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  goToDebts: (e: React.ChangeEvent<HTMLFormElement>) => void;
  isDisabled: boolean;
  isLoading: boolean;
  error: boolean;
  isVisibleModal: boolean;
  toggleModal: () => void;
  isVisibleModalLogin: boolean;
  toggleModalLogin: () => void;
  goToCreateAccount: () => void;
  isCaptchaVerified: boolean;
  isCaptchaMtcCaptcha: boolean;
  skipCaptchaValidation?: boolean;
  isLoadingCaptchaFlag?: boolean;
  /** Indica se está rodando dentro de uma WebView */
  isWebView?: boolean;
  /** Erros de validação dos campos */
  errors?: {
    email?: string;
    phone?: string;
  };
}

export default function IndexView({
  toggleMenu,
  isVisibleMenu,
  user,
  onChange,
  goToDebts,
  isDisabled,
  isLoading,
  error,
  isVisibleModal,
  toggleModal,
  isVisibleModalLogin,
  toggleModalLogin,
  goToCreateAccount,
  isCaptchaVerified,
  isCaptchaMtcCaptcha,
  skipCaptchaValidation = false,
  isLoadingCaptchaFlag = false,
  isWebView = false,
  errors = {}
}: PropsView) {

  const { t, Trans } = useI18n();
  const [isWebViewMenuOpen, setIsWebViewMenuOpen] = useState(false);

  const handleOpenWebViewMenu = useCallback(() => {
    setIsWebViewMenuOpen(true);
  }, []);

  const handleCloseWebViewMenu = useCallback(() => {
    setIsWebViewMenuOpen(false);
  }, []);

  const handleLoginClick = useCallback(() => {
    toggleModalLogin();
  }, [toggleModalLogin]);

  const handleGoBack = useCallback(() => {
    closeWebView();
  }, []);

  // Forçar exibição do menu via query param como fallback
  const urlParams = new URLSearchParams(window.location.search);
  const forceWebView = urlParams.get('webview') === 'true' || urlParams.get('webview') === '1';
  const showWebViewMenu = isWebView || forceWebView;

  const getMessage = () => {
    if (error) return t("pages:welcome:modalMessageDebtsNotFound");
    return t("pages:welcome:modalMessagePaymentAlreadyAutomated");
  }

  return (
    <div className="relative w-full h-full min-h-screen flex flex-col bg-brand-dark bg-[url('./assets/home-mobile-2x.png')] bg-no-repeat bg-[length:100%_auto] bg-[position:55%_-25%] md:bg-[url('./assets/home-desk-2x.png')] md:bg-cover md:bg-fixed md:bg-center">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-dark from-[3%] to-transparent to-[12%]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent from-[45%] via-brand-dark/85 via-[55%] to-brand-dark to-[65%]"
        aria-hidden
      />

      <main className="relative z-[1] w-full flex-1 flex flex-col">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[150px] bg-gradient-to-b from-brand-dark to-transparent z-0"
          aria-hidden
        />
        {showWebViewMenu && (
          <WebViewHeader
            onOpenMenu={handleOpenWebViewMenu}
            onGoBack={handleGoBack}
          />
        )}

        <header className="md:hidden relative flex items-center w-full h-[75px] border-b border-white/10">
          {!showWebViewMenu && (
            <button
              onClick={toggleMenu}
              type="button"
              className="flex items-center justify-center w-[75px] h-[75px] shrink-0 bg-brand-dark border-none outline-none appearance-none focus:outline-none "
              aria-controls="navbar-default"
              aria-expanded={isVisibleMenu}
            >
              {isVisibleMenu ? (
                <img
                  src="/svg/icon-close-white-2.0.svg"
                  alt="menu close"
                  className="w-[29px] h-[29px]"
                />
              ) : (
                <img
                  src="/svg/icon-menu-white-2.0.svg"
                  alt="menu"
                  className="w-[22px] h-[17px]"
                />
              )}
            </button>
          )}
          <div className="mx-6">
            <Icon className='dark' width="130" height="63" />
          </div>
          <div className="ml-auto mr-3">
            <LanguageSelector
              className="text-white"
              showFlag={true}
            />
          </div>
        </header>

        {/* --- Desktop header --- */}
        <header className="hidden md:flex w-full max-w-7xl mx-auto flex-row items-center p-3 border-b border-white/10">
          <div className="flex items-center w-1/2 justify-start m-4">
            <Icon className='dark' width="163" height="50" />
          </div>
          <nav className="flex w-1/2 justify-end">
            <ul className="flex flex-row items-center justify-end text-center p-0">
              <li className="p-2">
                <LanguageSelector
                  className="text-white"
                  showFlag={true}
                />
              </li>
              <li className="p-2">
                <button onClick={toggleModalLogin} className="text-white">{t("navigation:login")}</button>
              </li>
              <li className="p-2">
                <Link to="/faq" className="text-white">{t("navigation:faq")}</Link>
              </li>
              <li className="p-2">
                <button className='border border-white/40 rounded-theme-medium p-2 px-4 text-white' onClick={goToCreateAccount}>
                  {t("navigation:createAccount")}
                </button>
              </li>
            </ul>
          </nav>
        </header>

        <article className="mt-5 flex-1 flex p-3 justify-center items-center md:justify-around flex-col md:flex-row md:max-w-7xl md:mx-auto w-full">
          <div className="hidden md:block w-full justify-center flex-col p-3 text-left md:text-left max-w-[calc(780px)]">
            <h1 className="text-2xl md:text-[4rem] font-bold leading-none uppercase text-white">
              <Trans
                i18nKey="pages:welcome:heroSectionTitle"
                components={[<span className="text-secondary uppercase"></span>]}
              />
            </h1>
            <h2 className="text-sm md:text-lg text-white mt-4 max-w-[calc(380px)]">{t("pages:welcome:heroSectionSubtitle")}</h2>
          </div>
          <form
            className="w-full md:max-w-96 flex justify-start items-center flex-col gap-3 rounded-theme-xlarge border border-white/15 bg-[rgba(105,102,102,0.1)] backdrop-blur-[10px] p-5 md:p-6 min-h-96 md:max-h-calc(480px) mt-4 md:mt-0 font-secondary"
            onSubmit={isDisabled ? () => null : goToDebts}
          >
            <p className="w-full text-white text-sm leading-[18.2px] tracking-[-0.4px] font-secondary font-normal">
              {t("pages:welcome:labelTitle")}
            </p>
            <input
              value={user?.plate as string ?? ''}
              name="plate"
              placeholder={t('pages:welcome:placeholderPlate')}
              className="w-full h-12 rounded-theme-medium border border-white bg-foreground px-3.5 py-3 text-text placeholder:text-text-secondary font-primary text-sm"
              onChange={onChange}
              type="text"
              minLength={5}
              maxLength={7}
            />
            <div className="w-full">
              <input
                value={user?.email as string ?? ''}
                name="email"
                placeholder={t('pages:welcome:placeholderEmail')}
                className={`w-full h-12 rounded-theme-medium border px-3.5 py-3 text-text placeholder:text-text-secondary font-primary text-sm ${errors.email ? 'border-red-500' : 'border-white'} bg-foreground`}
                onChange={onChange}
                type="email"
                inputMode="email"
                required
              />
              {errors.email && (
                <span className="text-red-500 text-xs mt-1">{errors.email}</span>
              )}
            </div>
            <div className="w-full">
              <InputMask
                value={user?.phone as string ?? ''}
                name="phone"
                placeholder={t('pages:welcome:placeholderPhone')}
                className={`w-full h-12 rounded-theme-medium border px-3.5 py-3 text-text placeholder:text-text-secondary font-primary text-sm ${errors.phone ? 'border-red-500' : 'border-white'} bg-foreground`}
                onChange={onChange}
                type="text"
                inputMode="numeric"
                mask="(__) _____-____"
                replacement={{ _: /\d/ }}
                required
              />
              {errors.phone && (
                <span className="text-red-500 text-xs mt-1">{errors.phone}</span>
              )}
            </div>
            <div className="w-full">
              <p className="text-[12px] text-white leading-[14px] tracking-[-0.4px] font-secondary">
                <Trans
                  i18nKey="pages:welcome:labelTermsAndPrivacy"
                  components={[
                    <Link to="/terms" className="underline decoration-solid text-white" />,
                    <Link to="/policy" className="underline decoration-solid text-white" />,
                  ]}
                />
              </p>
            </div>
            {!skipCaptchaValidation && (
              <div className="w-full py-2 relative">
                <LoadingOverlay isLoading={isLoadingCaptchaFlag} bgColor="bg-foreground/40" />
                {isCaptchaMtcCaptcha ? (
                  <div className="mtcaptcha" id="mtcaptcha-intern" data-callback="onCaptchaSuccess"></div>
                ) : (
                  <div
                    className="g-recaptcha mx-auto"
                    data-sitekey={environment.recaptcha.siteKey}
                    data-size="normal"
                    data-theme="light"
                    data-callback="onRecaptchaVerify"
                    data-expired-callback="onRecaptchaExpired"
                    data-error-callback="onRecaptchaError"
                  />
                )}
              </div>
            )}
            <button
              type="submit"
              className={`w-full mt-4 h-[60px] text-white font-secondary text-base tracking-tight px-5 py-4 rounded-theme-medium transition-opacity ${isDisabled ? "bg-disabled text-disabled-text cursor-not-allowed" : "bg-primary hover:opacity-95"}`}
              disabled={isDisabled}>
              {t("pages:welcome:searchDebts")}
            </button>
            <div className="flex items-center justify-center h-[60px] gap-1 text-sm text-white font-secondary">
              <button type="button" className="underline decoration-solid font-bold" onClick={toggleModalLogin}>
                {t("navigation:login")}
              </button>
              <span className="text-white font-normal">{t("pages:welcome:orSeparator")}</span>
              <button type="button" className="underline decoration-solid font-bold" onClick={goToCreateAccount}>
                {t("navigation:createAccount")}
              </button>
            </div>
          </form>
        </article>
      </main>
      {isVisibleModalLogin && <LoginModal toggleModalLogin={toggleModalLogin} />}
      {isVisibleModal && <Modal
        title={t("pages:welcome:modalTitleAttention")}
        message={getMessage()}
        actionMessage={t("common:close")}
        action={toggleModal}
        actionSecond={toggleModal}
      />}
      {isLoading && <Loading />}

      <WebViewMenu isOpen={isWebViewMenuOpen} onClose={handleCloseWebViewMenu} onLogin={handleLoginClick} onCreateAccount={goToCreateAccount} />

      <div className="relative z-[1]">
        <Footer />
      </div>

      {isVisibleMenu && (
        <div className="fixed inset-0 z-50 mt-[75px] bg-brand-dark flex flex-col md:hidden">
          <nav className="flex-1 flex flex-col justify-start px-10 pt-20">
            <ul className="flex flex-col gap-10">
              {/* <li>
                <a href="#about" onClick={toggleMenu} className="text-white text-[22px] font-bold leading-tight">
                  {t("navigation:aboutToll")}
                </a>
              </li>
              <li>
                <a href="/faq" onClick={toggleMenu} className="text-white text-[22px] font-bold leading-tight">
                  {t("navigation:howItWorks")}
                </a>
              </li>
              <li>
                <a href="#highways" onClick={toggleMenu} className="text-white text-[22px] font-bold leading-tight">
                  {t("navigation:highways")}
                </a>
              </li>
              <li>
                <a href="#benefits" onClick={toggleMenu} className="text-white text-[22px] font-bold leading-tight">
                  {t("navigation:tagBenefits")}
                </a>
              </li> */}
              <li>
                <Link to="/faq" onClick={toggleMenu} className="text-white text-[22px] font-bold leading-tight">
                  {t("navigation:faq")}
                </Link>
              </li>
              <li>
                <button
                  onClick={() => { toggleMenu(); goToCreateAccount(); }}
                  className="text-white text-[22px] font-bold leading-tight text-left"
                >
                  {t("navigation:createAccount")}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}
